import { getSelectedCompanyId } from "../utils/tenantContext";
import { useTenantScope, normalizeScopePlatform } from "../scope/TenantScopeContext";
import { apiFetch } from "../api/client";
import React, { useEffect, useState } from "react";
import API from "../config";

const getAuthHeaders = () => {
  const token = localStorage.getItem("access_token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

function VulnerabilitiesPage() {
  const scope = useTenantScope();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const getCompanyId = () => {
    return getSelectedCompanyId() || "";
  };

  const loadData = async () => {
    try {
      setLoading(true);

      const companyId = getCompanyId();

      if (!companyId) {
        throw new Error(
          "No authenticated tenant is available."
        );
      }

      const res = await apiFetch(`/vulnerabilities/company/${companyId}`,
        { headers: getAuthHeaders() }
      );

      const data = await res.json();

      let rows = Array.isArray(data) ? data : [];

      if (scope.deviceUuid) {
        rows = rows.filter(
          (item) =>
            String(
              item.device_uuid ||
              item.endpoint_uuid ||
              item.asset_device_uuid ||
              ""
            ) === String(scope.deviceUuid)
        );
      }

      if (scope.platform && scope.platform !== "all") {
        rows = rows.filter(
          (item) =>
            normalizeScopePlatform(
              item.platform ||
              item.device_platform ||
              item.os ||
              item.operating_system
            ) === scope.platform
        );
      }

      setItems(rows);
    } catch (err) {
      console.error("Failed to load vulnerabilities:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadCompanyReport = () => {
    const companyId = getCompanyId();

    if (!companyId) {
      alert(
        "Select a tenant before generating the executive report."
      );
      return;
    }

    window.open(
      `${API}/reports/executive/${companyId}`,
      "_blank"
    );
  };

  useEffect(() => {
    loadData();
  }, [scope.deviceUuid, scope.platform]);

  const highCriticalCount = items.filter(
    (v) =>
      v.severity?.toLowerCase() === "high" ||
      v.severity?.toLowerCase() === "critical"
  ).length;

  const knownExploitedCount = items.filter(
    (v) => v.kev_known_exploited
  ).length;

  const averageAIScore = items.length
    ? Math.round(
        items.reduce(
          (sum, v) => sum + (v.ai_score || 0),
          0
        ) / items.length
      )
    : 0;

  return (
    <div>
      {/* Header Actions */}

      <div className="page-actions">
        <h2>Vulnerabilities</h2>

        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <button
            className="primary-button"
            onClick={loadData}
          >
            Refresh
          </button>

          <button
            className="primary-button"
            onClick={downloadCompanyReport}
          >
            Download Executive PDF Report
          </button>
        </div>
      </div>

      {/* Executive Risk Summary Cards */}

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "16px",
          marginBottom: "20px",
        }}
      >
        <div
          className="table-card"
          style={{ padding: "18px" }}
        >
          <h4>Total Applications</h4>
          <h2>{items.length}</h2>
        </div>

        <div
          className="table-card"
          style={{ padding: "18px" }}
        >
          <h4>High/Critical</h4>
          <h2>{highCriticalCount}</h2>
        </div>

        <div
          className="table-card"
          style={{ padding: "18px" }}
        >
          <h4>Known Exploited</h4>
          <h2>{knownExploitedCount}</h2>
        </div>

        <div
          className="table-card"
          style={{ padding: "18px" }}
        >
          <h4>Average AI Score</h4>
          <h2>{averageAIScore}</h2>
        </div>
      </div>

      {/* Severity Breakdown */}

      <div
        className="table-card"
        style={{
          padding: "18px",
          marginBottom: "20px",
        }}
      >
        <h3>Severity Breakdown</h3>

        {[
          "critical",
          "high",
          "medium",
          "low",
          "monitoring",
        ].map((level) => {
          const count = items.filter(
            (v) =>
              v.severity?.toLowerCase() === level
          ).length;

          const percent = items.length
            ? Math.round(
                (count / items.length) * 100
              )
            : 0;

          return (
            <div
              key={level}
              style={{ marginBottom: "12px" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  marginBottom: "4px",
                }}
              >
                <strong
                  style={{
                    textTransform: "capitalize",
                  }}
                >
                  {level}
                </strong>

                <span>
                  {count} ({percent}%)
                </span>
              </div>

              <div
                style={{
                  height: "10px",
                  background: "#e5e7eb",
                  borderRadius: "999px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${percent}%`,
                    background:
                      level === "critical"
                        ? "#7f1d1d"
                        : level === "high"
                        ? "#dc2626"
                        : level === "medium"
                        ? "#f59e0b"
                        : level === "low"
                        ? "#16a34a"
                        : "#64748b",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Main Vulnerability Table */}

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>Detected</th>
              <th>Device</th>
              <th>Application</th>
              <th>Version</th>
              <th>Exposure Type</th>
              <th>AI Score</th>
              <th>Recommendation</th>
              <th>CVE</th>
              <th>CVSS</th>
              <th>Known Exploited</th>
              <th>Severity</th>
              <th>Summary</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="12">
                  Loading...
                </td>
              </tr>
            ) : items.length > 0 ? (
              items.map((v, index) => (
                <tr key={index}>
                  <td>{v.detected || "N/A"}</td>

                  <td>{v.device || "N/A"}</td>

                  <td>{v.application || "N/A"}</td>

                  <td>{v.version || "N/A"}</td>

                  <td>
                    {v.exposure_type || "N/A"}
                  </td>

                  <td>
                    {v.ai_score ?? "N/A"}
                  </td>

                  <td>
                    {v.recommendation || "N/A"}
                  </td>

                  <td>{v.cve || "N/A"}</td>

                  <td>{v.cvss ?? "N/A"}</td>

                  <td>
                    {v.kev_known_exploited
                      ? "YES"
                      : "NO"}
                  </td>

                  <td>
                    <span
                      className={`severity-badge severity-${
                        v.severity
                          ? v.severity.toLowerCase()
                          : "unknown"
                      }`}
                    >
                      {v.severity || "Unknown"}
                    </span>
                  </td>

                  <td>
                    {v.summary || "No summary"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="12">
                  No vulnerabilities found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default VulnerabilitiesPage;